import { useState } from 'react'
import { _, deleteItem, getThankYouPage, load, parseNum } from '../../utils/helpers'
import { ExpressCheckoutElement, CardElement, useElements, useStripe, PaymentElement } from '@stripe/react-stripe-js'
import { Icon } from '../../parts/Icon'
import { useApp } from '../../context/useApp'
import {
	Stripe,
	StripeElements,
	StripeExpressCheckoutElementClickEvent,
	StripeExpressCheckoutElementConfirmEvent,
	StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js'
import { useDefaultDialogs } from '../../hooks/useDefaultDialogs'
import { DEFAULT_DIALOG_HANDLE, metrics, ROUTES, rumba } from '../../utils/symbols'
import { captureException, captureMessage } from '@sentry/react'
type Props = {
	clientSecret: string
}

export const PaymentDetails = ({ clientSecret }: Props) => {
	const {
		state: { booking, dialogHandles, shifts, products },
		actions: { navigate },
	} = useApp()

	const stripeState = useElements()
	const stripe = useStripe()

	const [loading, setLoading] = useState(false)
	const [availablePaymentMethods, setAvailablePaymentMethods] = useState(1)

	const handleExpressCheckoutReady = (event: StripeExpressCheckoutElementReadyEvent) => {
		setAvailablePaymentMethods(Object.values(event?.availablePaymentMethods ?? {}).filter(Boolean).length)
	}

	const defaultDialogs = useDefaultDialogs()
	const dialogHandle = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE
	const summary = booking.getBookingSummary()
	const shift = shifts.find((shift) => shift.id === booking.shiftId)
	const errors = booking.validatePersonalDetail({ shift: shift! })

	function checkRequiredProducts() {
		if (booking.hasTableSelection() && booking.step !== 'products') {
			return true
		}
		const requiredProduct = products.find((p) => p.required && booking.getProductQty(p.id) < booking.pax)
		if (!requiredProduct) return true
		dialogHandle.openDialog({
			dialog: 'required-product',
			product: requiredProduct,
			toAdd: booking.pax - booking.getProductQty(requiredProduct.id),
		})
		return false
	}

	const handleFormErrors = () => {
		if (!booking.hasTableSelection() && booking.step !== 'personal-details') {
			// check for required products
			const requiredProductsOk = checkRequiredProducts()
			if (!requiredProductsOk) {
				return
			}
			booking.setBookingStep('personal-details').render()
			navigate(ROUTES.PERSONAL_DETAILS)
		}

		errors.forEach((error, index) => {
			const inputContainer = document.getElementById(`${error.associatedTo}-container`) as HTMLDivElement
			if (!inputContainer) return

			if (index === 0) {
				const input = inputContainer.querySelector('input')
				if (input) input.focus()
				const container = document.querySelector('main')
				if (container) container.scrollTo({ top: inputContainer.offsetTop - 50, behavior: 'smooth' })
			}

			inputContainer.classList.add('error-visible')
		})
	}

	async function handlePayment(event?: StripeExpressCheckoutElementConfirmEvent) {
		if (loading) {
			console.warn('Payment in progress...')
			return
		}
		try {
			setLoading(true)
			const result = await check()
			if (result.error) {
				setLoading(false)
				console.error('Check result is not ok: ', result.message)
				if (event) {
					event.paymentFailed({
						reason: 'fail',
						message: result.message,
					})
				}
				return
			}

			const { stripe, stripeState } = result
			await pay(stripe!, stripeState!)
		} catch (err) {
			captureException(err)
			defaultDialogs.show('no-tables-after-payment')
			setLoading(false)
			if (event) {
				event.paymentFailed({
					reason: 'fail',
					message: (err as any).message,
				})
			}
		}
	}

	async function handleExpressCheckoutClick(event: StripeExpressCheckoutElementClickEvent) {
		const result = await check()
		if (result.error) {
			console.error('Express checkout rejected due error: ', result.message)
			event.reject()
			return
		}
		event.resolve()
	}

	async function check() {
		handleFormErrors()
		if (!clientSecret || errors.length || !stripe || !stripeState) {
			return {
				error: true,
				reason: 'fail',
				message: _('0pa_error_1'),
			}
		}
		const tablesCheck = await rumba.checkTables(booking.getBookingDataByReference())
		if (tablesCheck.result !== 'ok' && tablesCheck.duplicated) {
			deleteItem('booking-data')
			window.location.href = getThankYouPage()
			return {
				error: true,
				reason: 'fail',
				message: _('0pa_error_2'),
			}
		}
		if (tablesCheck.result !== 'ok') {
			defaultDialogs.show('no-tables')
			return {
				error: true,
				reason: 'fail',
				message: _('0pa_error_3'),
			}
		}
		await metrics.paymentMethodSelectEvent(booking.getBookingDataByReference())
		return { stripe, stripeState }
	}

	async function pay(stripe: Stripe, stripeState: StripeElements, event?: StripeExpressCheckoutElementConfirmEvent) {
		const result = await stripe.confirmPayment({
			elements: stripeState,
			redirect: 'if_required',
			confirmParams: {
				payment_method_data: {
					billing_details: {
						name: booking.name,
						email: booking.email,
						address: {
							country: booking.country || 'ES',
							// Include an empty string for postal_code to satisfy the requirement if address is partially overridden
							postal_code: '',
						},
					},
				},
			},
		})
		if (result.error) {
			await metrics.paymentErrorEvent(result.error.message ?? '', booking.getBookingDataByReference())
			dialogHandle.openDialog({
				dialog: 'error',
				className: 'error w-100 h-100 df aic jcc',
				message: result.error.message ?? _('0ge_anune__'),
				button: _('0ge_under__'),
				callback: () => dialogHandle.closeDialog(),
			})
			if (event) {
				event.paymentFailed({
					reason: 'fail',
					message: result.error.message,
				})
			}
			setLoading(false)
			return
		}
		await success(result.paymentIntent.id)
		return
	}

	async function success(intent: string) {
		await metrics.paymentEvent(booking.getBookingDataByReference())
		//save booking only if payment succeded
		const saveBookingResponse = await rumba.saveBooking({
			...booking.getBookingDataByReference(),
			paymentIntentId: intent,
		})
		if (saveBookingResponse.result !== 'ok') {
			captureMessage(`saveBooking not ok: ${JSON.stringify(saveBookingResponse)}`)
			defaultDialogs.show('no-tables-after-payment')
			setLoading(false)
			return
		}
		deleteItem('booking-data')
		window.location.href = getThankYouPage()
	}

	const showExpressCheckoutProvider = availablePaymentMethods !== 0

	return (
		<fieldset id="payment-details" className="pt-8 pb-7 r3 mb-8 mt-0">
			<legend className="text s20-600 mb-7">{_('5pi_enter__')}</legend>

			{/* Express Checkout (Apple Pay, Google Pay) */}
			{showExpressCheckoutProvider && (
				<div className="express-checkout-wrapper">
					<ExpressCheckoutElement
						onConfirm={handlePayment}
						onClick={handleExpressCheckoutClick}
						onReady={handleExpressCheckoutReady}
						options={{
							// paymentMethods: { link: "never" },
							layout: {
								maxColumns: window.innerWidth < 500 ? 1 : 2,
								overflow: 'never',
							},
							buttonType: {
								applePay: 'buy',
								googlePay: 'buy',
							},
							buttonHeight: 50,
						}}
					/>
				</div>
			)}

			{/* Divider */}
			{showExpressCheckoutProvider && (
				<div className="payment-divider df aic my-8">
					<div className="divider-line" style={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }}></div>
					<span className="text soft s14-500 px-4 upper">{_('0pa_label__')}</span>
					<div className="divider-line" style={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }}></div>
				</div>
			)}

			{/* Payment Element - Card details */}
			<div className="compound-input payment-element-wrapper">
				{/* <CardElement options={{ style: { base: { lineHeight: "50px" } }, hidePostalCode: true }} /> */}
				<PaymentElement
					options={{
						fields: {
							billingDetails: { address: { country: 'never' } },
						},
						layout: { type: 'tabs', defaultCollapsed: true },
						wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
					}}
				/>
			</div>
			<button
				onClick={(e) => handlePayment()}
				style={{ height: '50px' }}
				className="button r3 w-100 px-10 text s20-500 action-2 b-0 button-hover capitalized mt-7"
			>
				<span className="mr-5">{_('5su_buyti__')}</span>
				<span>{parseNum('EUR', summary.total)}</span>
			</button>
			<div className={`df aic jcfe mt-6 d-ib`} aria-hidden>
				<p className="text soft s14-500 mr-7 upper nw">{_('5su_secur__')}: </p>
				<div className="df aic">
					<Icon icon="visa" className="mr-5" />
					<Icon icon="mastercard" className="mr-5" />
				</div>
			</div>

			{/* Security badges */}
		</fieldset>
	)
}
